import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import type {
  IParamsGroupForceCreate,
  IResponseConversationGroupError,
} from 'entities/Conversation/api/types'
import { Contact } from 'entities/Contacts/model/Contact'
import { type Conversation, ConversationsApi } from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class ConversationGroupError {
  private _modalId = 'contact_already_in_another_group'
  private _notAddedParticipantsMap: Map<number, Contact> = new Map()
  private readonly _params: IParamsGroupForceCreate | null = null
  private _loading = false

  constructor(response: IResponseConversationGroupError, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    response.not_added_participants.map((item) => {
      this._notAddedParticipantsMap.set(item.id, new Contact(item))
    })

    this._params = {
      contact_id: response.contact_id,
      participants: response.participants,
      team_id: response.team_id,
      number_id: response.number_id,
      participant_details: response.participant_details,
    }
  }

  showModal = (): Promise<Conversation | null> => {
    return new Promise((resolve, reject) => {
      modalStore.addModal({
        id: this._modalId,
        title: 'Contact already in another group',
        desc: this._description,
        type: ModalTypeList.INFO,
        showCloseButton: false,
        disabledOnAllClose: true,
        disabledOverlayClose: true,
        disabledOnEscClose: true,
        primaryAction: {
          text: 'Continue',
          disabled: this._disabled,
          onAction: async () => {
            try {
              const item = await this._createConversationForce()

              resolve(item || null)
            } catch (e) {
              reject(e)
              toastStore.add({
                id: nanoid(),
                type: 'error',
                title: 'Something went wrong, try again!',
              })
              this._closeModal()
            }
          },
        },
        secondaryAction: {
          text: 'Close',
          onAction: () => {
            resolve(null)
            this._closeModal()
          },
        },
      })
    })
  }

  private _closeModal = () => {
    modalStore.closeModal(this._modalId)
  }

  private _createConversationForce = async () => {
    if (!this._params) return
    if (this._loading) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await ConversationsApi.createGroupForce(this._params)

      this._entities.conversationStore.updateItem(data)

      return this._entities.conversationStore.getItem(data.id)
    } catch (e) {
      logger.error(e)

      throw e
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private get _disabled() {
    return !this._params
  }

  private get _description() {
    const contactNames = this._notAddedParticipants.reduce((state, item, index) => {
      if (this._notAddedParticipants.length === 1) {
        state += `${item.name} `
      } else if (this._notAddedParticipants.length === index + 1) {
        state += `and ${item.name} `
      } else {
        state += `${item.name}, `
      }

      return state
    }, '')

    return `${contactNames}are currently part of another active group conversation. Adding them here will close their previous group chats.`
  }

  private get _notAddedParticipants() {
    return Array.from(this._notAddedParticipantsMap.values())
  }
}
